/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
import get from 'lodash/get';
import { AxiosError } from 'axios';

import { ERROR_MESSAGES, getErrorMessage } from '../../utils';
import {
  ERR_ALREADY_REGISTERED,
  KYC_ERROR_CODES
} from '../../../components/WidgetPage/constants';
import {
  GetUserActionType,
  InitializeInAppType
} from '../../global-actions/types';
import { Analytics } from '../../../ModularReduxAnalytics';

const reducers = {
  appAnswerFailure: (app, payload: { response: any }) => {
    let answerFailureMessage = '';
    if (payload.response && payload.response.data) {
      answerFailureMessage = Array.isArray(payload.response.data.message)
        ? payload.response.data.message[0].message
        : payload.response.data.message;
    }

    return {
      ...app,
      answerLoading: false,
      submitPlaidLoading: false,
      errorCode: payload.response ? payload.response.data.code : '',
      registeredEmailError:
        answerFailureMessage ===
        'The email address you entered is already associated with a Greenlight account. Please login with that account or contact support.',
      isFirstScreenForReturningUser: false
    };
  },

  appAnswerLoading: app => ({
    ...app,
    answerLoading: true,
    message: ''
  }),

  appAnswerSuccess: (
    app,
    payload: {
      nextQuestions: string[];
      flowId: string;
      groupId: string;
      totalGroupsInFlow: number;
      errors: any[];
      unresolvedErrors: any[];
      prefilled: any[];
      extras: any[];
      needsVerification: boolean;
    }
  ) => ({
    ...app,
    questions: payload.nextQuestions,
    completeUser: !!(
      payload.nextQuestions && payload.nextQuestions.indexOf('complete') !== -1
    ),
    flowId: payload.flowId,
    groupId: payload.groupId,
    totalGroupsInFlow: payload.totalGroupsInFlow,
    submitPlaidLoading: false,
    answerLoading: false,
    message: '',
    unresolvedErrors: payload.errors || payload.unresolvedErrors || [],
    prefilledAnswers: payload.prefilled || {},
    extras: payload.extras ? payload.extras[0] : null,
    needsVerification: payload.needsVerification,
    loading: false,
    isFirstScreenForReturningUser: false
  }),

  asyncFailure: (app, message) => ({
    ...app,
    loading: false,
    error: true,
    message
  }),

  asyncLoading: app => ({
    ...app,
    loading: true
  }),

  asyncSuccess: (app, payload: { accessToken: string }) => ({
    ...app,
    loading: false,
    error: false,
    regAccessToken: payload.accessToken || app.regAccessToken
  }),

  clearError: app => ({
    ...app,
    error: null,
    message: null
  }),

  appCompleteFailure: (app, payload: { code: string }) => ({
    ...app,
    errorCode: payload ? payload.code : ''
  }),

  appCompleteSuccess: app => ({
    ...app,
    message: ''
  }),

  completeResetKBAFlow: app => ({
    ...app,
    resetKBAFlow: false
  }),

  createError: (
    app,
    {
      message,
      error = true
    }: { message: string; registrationStep?: string; error?: boolean }
  ) => ({
    ...app,
    error,
    message,
    timeoutError:
      message === 'Your session has expired. Please log in again.'
        ? message
        : ''
  }),

  setSmsNotificationsOptedStatus: (
    app,
    { optedStatus }: { optedStatus: string }
  ) => ({
    ...app,
    smsNotificationsOptedStatus: optedStatus
  }),

  displayLegalAddress: (app, payload: { display: boolean }) => ({
    ...app,
    displayLegalAddress: payload
  }),

  appGetAnswersSuccess: (app, payload: { answers: any[] }) => ({
    ...app,
    answers: payload
  }),

  appGetAnswersFailure: app => ({
    ...app,
    isFirstScreenForReturningUser: false
  }),

  appGetPricingPlanFailure: (app, payload: { response: any }) => {
    const getPricingPlanErrorMessage =
      get(payload, 'response.data.message') ||
      ERROR_MESSAGES.defaultGetPricingPlanFailure;

    return {
      ...app,
      message: getPricingPlanErrorMessage
    };
  },

  appGetProveRedirectUriFailure: app => ({
    ...app,
    proveMobileAuthState: 'failure'
  }),

  appGetProveRedirectUriLoading: app => ({
    ...app,
    proveMobileAuthState: 'initial-redirect'
  }),

  appGetProveRedirectUriSuccess: (app, payload: { redirect?: any }) => ({
    ...app,
    proveMobileAuthState: 'pending',
    proveRedirectUri: payload.redirect?.destination
  }),

  appGetUserSuccess: (app, payload: { user: any; token: string }) => {
    let nextRegStep: string = payload.user.nextRegisterStep;
    if (
      payload.user.nextRegisterStep === 'complete' &&
      payload.user.role === 'child'
    ) {
      nextRegStep = 'complete_child';
    }

    return {
      ...app,
      nextRegStep,
      jwt: payload.token
    };
  },

  setNoPhoneRegEntry: app => ({
    ...app,
    noPhoneRegEntry: true
  }),

  setLinkSource: (app, payload: { linkSource: string }) => ({
    ...app,
    linkSource: payload.linkSource
  }),

  appInitializeFailure: (app, payload: { response: any }) => ({
    ...app,
    errorCode: payload ? payload.response.status : '',
    regAccessToken: payload.response.status === 401 ? null : app.regAccessToken,
    initializeLoading: false
  }),

  appInitializeSuccess: (
    app,
    payload: {
      errors?: any[];
      unresolvedErrors?: any[];
      nextQuestions: any[];
      prefilled?: any[];
      userJWT: string;
      registrationToken: string;
    }
  ) => {
    const error =
      payload.unresolvedErrors &&
      payload.unresolvedErrors.length > 0 &&
      payload.unresolvedErrors.some(err => err.code !== ERR_ALREADY_REGISTERED);

    const partialKYCError =
      payload.unresolvedErrors &&
      payload.unresolvedErrors.length > 0 &&
      payload.unresolvedErrors.some(
        err => err.code === KYC_ERROR_CODES.partial
      );

    const message = partialKYCError
      ? 'Please re-enter your information and try again'
      : app.message;
    const updateIfExists: Array<string> = [
      'flowId',
      'groupId',
      'totalGroupsInFlow',
      'registrationToken'
    ];
    const updatedState = updateIfExists.reduce(
      (prev: Record<string, string | number>, curr) => ({
        ...prev,
        [curr]: payload[curr] || app[curr]
      }),
      {}
    );

    return {
      ...app,
      ...updatedState,
      error,
      message,
      questions: payload.nextQuestions,
      unresolvedErrors: payload.unresolvedErrors || [],
      prefilledAnswers: payload.prefilled || {},
      completeUser:
        payload.nextQuestions &&
        payload.nextQuestions.indexOf('complete') !== -1,
      completeOnInit:
        payload.nextQuestions &&
        payload.nextQuestions.indexOf('complete') !== -1 &&
        (!Array.isArray(payload.unresolvedErrors) ||
          !payload.unresolvedErrors.length) &&
        (!Array.isArray(payload.errors) || !payload.errors.length),
      resetKBAFlow: !!(
        payload.unresolvedErrors &&
        payload.unresolvedErrors.length > 0 &&
        payload.unresolvedErrors.find(
          err =>
            err.code === KYC_ERROR_CODES.partial ||
            err.code === KYC_ERROR_CODES.challenge
        )
      ),
      stargatewayKey: payload.userJWT ? payload.userJWT : app.stargatewayKey,
      registrationToken: payload.registrationToken || app.registrationToken,
      initializeLoading: false
    };
  },

  appSetInitializeInAppProperties: (
    app,
    payload: InitializeInAppType['payload']
  ) => {
    const inAppPreReg = payload.preregisteredUserId && !payload.authToken;

    return {
      ...app,
      regAccessToken: payload.accessToken,
      stargatewayKey: payload.userToken,
      appOs: payload.appOs,
      appVersion: payload.appVersion,
      embeddedWebreg: true,
      nextRegStep: 'register',
      inAppPreReg
    };
  },

  invalidatePma: app => ({
    ...app,
    proveMobileAuthState: 'invalidated',
    regAccessToken: null,
    needsAccessRequest: true
  }),

  appQueryGetUserSuccess: (app, payload: { user: any; token: string }) => {
    return {
      ...app,
      jwt: payload.token
    };
  },

  appResolveErrorLoading: (app, payload) => ({
    ...app,
    message: ''
  }),

  appResolveErrorFailure: (app, payload?: { code: string }) => ({
    ...app,
    errorCode: payload ? payload.code : ''
  }),

  appResolveErrorSuccess: (app, payload: { unresolvedErrors?: any[] }) => ({
    ...app,
    message: '',
    unresolvedErrors: payload.unresolvedErrors || []
  }),

  startLoading: (app, payload: { isPlaid?: boolean }) => ({
    ...app,
    loading: true,
    submitPlaidLoading: payload.isPlaid || false
  }),

  stopLoading: app => ({
    ...app,
    loading: false
  }),

  appVerifyPromoCodeFailure: (app, payload: { response?: any }) => ({
    ...app,
    errorCode: payload.response ? payload.response.data.code : ''
  }),

  appVerifyPromoCodeSuccess: (app, payload: { response?: any }) => ({
    ...app,
    promoCode: payload.response.data
  }),

  setTogglesLoaded: (app, payload: { loaded: boolean }) => ({
    ...app,
    togglesDirty: !payload.loaded
  }),

  setStargatewayKey: (app, payload: { stargatewayKey: string }) => ({
    ...app,
    stargatewayKey: payload.stargatewayKey
  }),

  setShortcut: (app, payload: { shortcut: string; shortcutPlan: string }) => ({
    ...app,
    shortcut: payload.shortcut,
    shortcutPlan: payload.shortcutPlan
  }),

  setPmaFinalTarget: (app, payload: { regAccessToken: string }) => ({
    ...app,
    proveMobileAuthState: 'success',
    regAccessToken: payload.regAccessToken
  }),

  setPmaState: (app, pmaState: string) => ({
    ...app,
    proveMobileAuthState: pmaState
  }),

  answer: (
    app,
    payload: {
      accessToken?: string | null;
      questions: any[];
      answers: any[];
      resolveErrorId?: string;
    }
  ) => app,

  complete: app => app,

  checkValidEmail: (app, email: string) => app,

  emailVerification: (
    app,
    payload: {
      verificationToken: string;
      userId: string;
    }
  ) => app,

  verifyPromoCode: (
    app,
    payload: {
      code: string;
    }
  ) => app,

  validatePlaceholderToken: (
    app,
    payload: {
      userId: string;
      placeholderToken: string;
    }
  ) => app,

  initialize: (
    app,
    payload: {
      accessToken: string | null;
      uniqueId: string | null;
      role: string | null;
      preregisteredUserId: string | null;
      authToken: string;
      dob?: string;
      partnerSource: string | null;
      registrationId: string | null;
      isFcc?: boolean;
      plan?: string;
    }
  ) => ({
    ...app,
    initializeLoading: true
  }),

  getAnswers: app => app,

  getPricingPlan: app => app,

  getProveRedirectUri: (
    app,
    payload: {
      mockPma: {
        enabled: boolean;
        state: string;
        phone: string;
      };
    }
  ) => app,

  getUser: (app, registrationToken: GetUserActionType['payload']) => app,
  appGetUserFailure: (app, error: AxiosError) => {
    return {
      ...app,
      loading: false,
      error: true,
      message: getErrorMessage(error)
    };
  },

  queryGetUser: app => app,

  resolveError: (app, payload: { errorId: string }) => app,

  setShowFccPreReg: (app, showFccPreReg: boolean) => ({
    ...app,
    showFccPreReg
  }),

  // wria passthrough resolvers
  registrationFailedAppMode: (
    app,
    payload: { appOs?: string; error?: Error }
  ) => app,
  launchNativePlaidFlow: app => app,
  initializeInApp: (app, payload: InitializeInAppType['payload']) => app,
  registrationReauth: app => app,
  postRegComplete: app => app,
  linkToExternalBrowser: app => app,
  failedWriaUrlValidation: app => ({
    ...app,
    shouldRedirectToExternalBrowser: true
  }),
  setRegistrationIdFromPartner: (app, registrationId: string) => ({
    ...app,
    registrationId
  }),
  getFailureReason: app => app,

  setIsKycRetryAttempt: (app, payload: { isKycRetryAttempt: boolean }) => ({
    ...app,
    isKycRetryAttempt: payload.isKycRetryAttempt
  }),
  subscribeSMS: app => app,
  trackCustomEvent: (
    app,
    payload: {
      eventName: string;
      eventProperties: any;
      userAlias: { alias_name: string; alias_label: string };
    }
  ) => app,
  identifyUser: (
    app,
    payload: {
      userAlias: {
        alias_name: string;
        alias_label: string;
      };
      externalId: string;
    }
  ) => app,
  identifyUserFailure: (app, error: AxiosError) => {
    Analytics.trackEvent('Registration Error Triggered', {
      eventProps: {
        registration_step: 'IdentifyUser',
        error_message: getErrorMessage(error)
      }
    });
    return app;
  },
  refetchServersideTogglesAfterLogin: (
    app,
    payload: {
      anonymousId: string;
      attributes: any;
    }
  ) => app,
  setShouldRefetchServersideToggles: (
    app,
    payload: { shouldRefetchServersideToggles: boolean }
  ) => ({
    ...app,
    shouldRefetchServersideToggles: payload.shouldRefetchServersideToggles
  }),
  setHasCheckedForLtaRedirectOnLoad: app => ({
    ...app,
    hasCheckedForLtaRedirectOnLoad: true
  })
};

export default reducers;
